// @refresh reset
/**
 * context/WebSocketContext.tsx
 * ─────────────────────────────
 * Global, app-wide WebSocket provider.
 *
 * Architecture
 * ──────────────
 *  - ONE persistent WS connection per browser tab (not per component).
 *  - Components subscribe to specific message types via subscribe().
 *  - Auto-reconnect with exponential back-off (1 → 2 → 4 → … → 30 s max).
 *  - Reconnects when:
 *    • Network drops
 *    • JWT changes (user logs in/out)
 *    • Tab becomes visible after sleep (Visibility API)
 *  - On reconnect, fires a "reconcile" callback so contexts (AlertContext)
 *    can do a one-time HTTP fetch to catch up on missed messages.
 *
 * Usage
 * ──────
 *  const { status, subscribe } = useWebSocket();
 *  useEffect(() => {
 *    const unsub = subscribe('ALERT_CREATED', (payload) => { ... });
 *    return unsub;                      // cleanup on unmount
 *  }, [subscribe]);
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type WsStatus = "connecting" | "connected" | "disconnected" | "reconnecting";

type MessageHandler = (payload: unknown) => void;

interface WebSocketContextValue {
  /** Current connection status */
  status: WsStatus;
  /**
   * Subscribe to a message type.
   * @param type   The `message.type` string to listen for (e.g. "ALERT_CREATED")
   * @param handler Callback receiving `message.payload`
   * @returns      Unsubscribe function — call in useEffect cleanup
   */
  subscribe: (type: string, handler: MessageHandler) => () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export const useWebSocket = (): WebSocketContextValue => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error("useWebSocket must be used within WebSocketProvider");
  return ctx;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
const WS_BASE  = API_BASE.replace(/^http/, "ws");

// Build the WebSocket URL
// Handles both "http://host:8000" and "http://host/api/v1" base URLs
const WS_PATH  = WS_BASE.includes("/api/v1") ? "/notifications/ws" : "/api/v1/notifications/ws";

function buildWsUrl(token: string): string {
  return `${WS_BASE}${WS_PATH}?token=${encodeURIComponent(token)}`;
}

function getStoredToken(): string | null {
  // The app stores tokens in sessionStorage (per-tab isolation)
  // See: frontend/src/api/index.js → saveTokens()
  return sessionStorage.getItem("access_token") || null;
}

// Back-off: 1s → 2s → 4s → 8s → 16s → 30s (capped)
function getBackoffMs(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt), 30_000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface WebSocketProviderProps {
  children: React.ReactNode;
  /** Optional: called after every successful reconnect for reconciliation */
  onReconnect?: () => void;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  onReconnect,
}) => {
  const [status, setStatus] = useState<WsStatus>("disconnected");

  // Subscriber registry: type → Set<handler>
  const listenersRef = useRef<Map<string, Set<MessageHandler>>>(new Map());

  // Live WS reference
  const wsRef = useRef<WebSocket | null>(null);

  // Reconnect control
  const attemptRef       = useRef(0);
  const reconnectTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef     = useRef(true);

  // Track last known token to detect login/logout
  const lastTokenRef = useRef<string | null>(null);

  // ── Dispatch incoming messages to subscribers ──────────────────────────────
  const dispatch = useCallback((rawData: string) => {
    try {
      const msg = JSON.parse(rawData) as { type?: string; payload?: unknown };
      const type = msg?.type;
      if (!type) return;

      const handlers = listenersRef.current.get(type);
      if (!handlers) return;

      handlers.forEach((h) => {
        try {
          h(msg.payload);
        } catch (err) {
          console.error(`[WS] Handler error for type=${type}:`, err);
        }
      });
    } catch (e) {
      console.warn("[WS] Could not parse message:", rawData, e);
    }
  }, []);

  // ── Open connection ────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!isMountedRef.current) return;

    // Cancel any pending reconnect timer
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    // Close existing stale socket
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      existing.onclose = null; // prevent double-reconnect
      existing.close();
    }

    const token = getStoredToken();
    if (!token) {
      setStatus("disconnected");
      return;
    }

    lastTokenRef.current = token;
    setStatus(attemptRef.current === 0 ? "connecting" : "reconnecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(buildWsUrl(token));
    } catch (e) {
      console.error("[WS] Failed to create WebSocket:", e);
      scheduleReconnect();
      return;
    }

    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) { ws.close(); return; }
      console.log("[WS] Connected ✓");
      setStatus("connected");
      const wasReconnect = attemptRef.current > 0;
      attemptRef.current = 0;
      if (wasReconnect && onReconnect) {
        onReconnect();
      }
    };

    ws.onmessage = (event) => {
      dispatch(event.data);
    };

    ws.onerror = (ev) => {
      console.warn("[WS] Socket error:", ev);
    };

    ws.onclose = (ev) => {
      if (!isMountedRef.current) return;
      console.log(`[WS] Disconnected (code=${ev.code} reason=${ev.reason || "—"})`);
      setStatus("disconnected");
      // 1000 = Normal closure (user logged out intentionally) — don't reconnect
      if (ev.code !== 1000) {
        scheduleReconnect();
      }
    };
  }, [dispatch, onReconnect]);

  // ── Schedule a reconnect with back-off ─────────────────────────────────────
  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;
    const delay = getBackoffMs(attemptRef.current);
    attemptRef.current += 1;
    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${attemptRef.current})`);
    setStatus("reconnecting");
    reconnectTimer.current = setTimeout(() => {
      if (isMountedRef.current) connect();
    }, delay);
  }, [connect]);

  // ── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;

    // React StrictMode fires this twice in dev; the second call re-mounts cleanly.
    connect();

    // Reconnect on tab visibility restored
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        const ws = wsRef.current;
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          attemptRef.current = 0; // reset back-off for manual re-focus
          connect();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isMountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.close(1000, "Component unmounted");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-connect when the JWT token changes (login / logout / refresh) ───────
  useEffect(() => {
    const interval = setInterval(() => {
      const current = getStoredToken();
      if (current !== lastTokenRef.current) {
        console.log("[WS] Token changed — reconnecting …");
        attemptRef.current = 0;
        connect();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [connect]);

  // ── subscribe() ───────────────────────────────────────────────────────────
  const subscribe = useCallback(
    (type: string, handler: MessageHandler): (() => void) => {
      const map = listenersRef.current;
      if (!map.has(type)) map.set(type, new Set());
      map.get(type)!.add(handler);

      // Return unsubscribe
      return () => {
        const set = map.get(type);
        if (set) {
          set.delete(handler);
          if (set.size === 0) map.delete(type);
        }
      };
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <WebSocketContext.Provider value={{ status, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
